import React, { useState, useEffect, useRef } from "react";
import "./Results.css";
import { useNavigate } from "react-router-dom";
import Chart from "chart.js/auto";
import empathyDefinitions from "../empathyDefinitions";
import ToolFeedback from "../ToolFeedback/ToolFeedback";
import UserRecommendations from "../../Dashboard/UserDashboard/UserRecommendations/UserRecommendations";

function Results() {
  const [score, setScore] = useState([]);
  const [displayProfile, setDisplayProfile] = useState([]);
  const [pracDisplayProfile, setPracDisplayProfile] = useState([]);
  const [userDisplayProfile, setUserDisplayProfile] = useState([]);
  const chartRef = useRef(null);
  const canvasRef = useRef(null);

  const empathyScoreHighThreshold = 0.6;
  const empathyScoreLowThreshold = 0.3;

  // Fetch quiz data from the backend
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_FLASK_API_URL}/score`, {
      method: "GET",
      credentials: "include",
    }) //
      .then((response) => response.json())
      .then((data) => setScore(data)) // Update state with fetched data
      .catch((error) => console.error("Error fetching data:", error)); // Log any errors
  }, []);

  // handling the logout button
  const handleLogout = () => {
    fetch(`${process.env.REACT_APP_BACKEND_FLASK_API_URL}/logout`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data.message);
        navigate(`/`);
      })
      .catch((error) => console.error("Error logging out:", error));
  };

  useEffect(() => {
    if (!score || score.length === 0) return; // Prevent errors if score is undefined

    if (Array.isArray(score[1])) {
      // practitioner code
      let pracCode = "";
      pracCode +=
        score[0][0]["Cognitive"] >
        score[0][2]["Cognitive"] * empathyScoreHighThreshold
          ? "H"
          : score[0][0]["Cognitive"] <
            score[0][2]["Cognitive"] * empathyScoreLowThreshold
          ? "L"
          : "M";
      pracCode +=
        score[0][0]["Affective"] >
        score[0][2]["Affective"] * empathyScoreHighThreshold
          ? "H"
          : score[0][0]["Affective"] <
            score[0][2]["Affective"] * empathyScoreLowThreshold
          ? "L"
          : "M";
      pracCode +=
        score[0][0]["Empathic Response"] >
        score[0][2]["Empathic Response"] * empathyScoreHighThreshold
          ? "H"
          : score[0][0]["Empathic Response"] <
            score[0][2]["Empathic Response"] * empathyScoreLowThreshold
          ? "L"
          : "M";

      fetch(
        `${process.env.REACT_APP_BACKEND_FLASK_API_URL}/profile?code=${pracCode}&type=practitioner`,
        {
          method: "GET",
        }
      )
        .then((response) => response.json())
        .then((data) => setPracDisplayProfile(data))
        .catch((error) =>
          console.error("Error fetching practitioner profile:", error)
        );

      // user code
      let userCode = "";
      userCode +=
        score[1][0]["Cognitive"] >
        score[1][2]["Cognitive"] * empathyScoreHighThreshold
          ? "H"
          : score[1][0]["Cognitive"] <
            score[1][2]["Cognitive"] * empathyScoreLowThreshold
          ? "L"
          : "M";
      userCode +=
        score[1][0]["Affective"] >
        score[1][2]["Affective"] * empathyScoreHighThreshold
          ? "H"
          : score[1][0]["Affective"] <
            score[1][2]["Affective"] * empathyScoreLowThreshold
          ? "L"
          : "M";
      userCode +=
        score[1][0]["Empathic Response"] >
        score[1][2]["Empathic Response"] * empathyScoreHighThreshold
          ? "H"
          : score[1][0]["Empathic Response"] <
            score[1][2]["Empathic Response"] * empathyScoreLowThreshold
          ? "L"
          : "M";

      fetch(
        `${process.env.REACT_APP_BACKEND_FLASK_API_URL}/profile?code=${userCode}&type=user`,
        {
          method: "GET",
        }
      )
        .then((response) => response.json())
        .then((data) => setUserDisplayProfile(data))
        .catch((error) => console.error("Error fetching user profile:", error));
    } else {
      let code = "";

      code +=
        score[0]["Cognitive"] >
        score[2]["Cognitive"] * empathyScoreHighThreshold
          ? "H"
          : score[0]["Cognitive"] <
            score[2]["Cognitive"] * empathyScoreLowThreshold
          ? "L"
          : "M";
      code +=
        score[0]["Affective"] >
        score[2]["Affective"] * empathyScoreHighThreshold
          ? "H"
          : score[0]["Affective"] <
            score[2]["Affective"] * empathyScoreLowThreshold
          ? "L"
          : "M";
      code +=
        score[0]["Empathic Response"] >
        score[2]["Empathic Response"] * empathyScoreHighThreshold
          ? "H"
          : score[0]["Empathic Response"] <
            score[2]["Empathic Response"] * empathyScoreLowThreshold
          ? "L"
          : "M";
      fetch(
        `${process.env.REACT_APP_BACKEND_FLASK_API_URL}/profile?code=${code}&type=${score[1]}`,
        {
          method: "GET",
        }
      )
        .then((response) => response.json())
        .then((data) => setDisplayProfile(data))
        .catch((error) =>
          console.error("Error fetching single profile:", error)
        );
    }
  }, [score]);

  useEffect(() => {
    if (!score || score.length === 0) return;

    const data = {
      labels: ["Cognitive", "Affective", "Empathic Response"],
      datasets: Array.isArray(score[1]) // make sure this isn't length for other quiz stored
        ? [
            {
              label: "Practitioner-to-Practitioner",
              data: [
                Math.round(
                  (score[0][0]["Cognitive"] / score[0][2]["Cognitive"]) * 100
                ),
                Math.round(
                  (score[0][0]["Affective"] / score[0][2]["Affective"]) * 100
                ),
                Math.round(
                  (score[0][0]["Empathic Response"] /
                    score[0][2]["Empathic Response"]) *
                    100
                ),
              ],
              backgroundColor: "rgba(75, 192, 192, 0.2)",
              borderColor: "rgb(75, 192, 192)",
              pointBackgroundColor: "rgb(75, 192, 192)",
              borderWidth: 2,
              fill: true,
            },
            {
              label: "Practitioner-to-User",
              data: [
                Math.round(
                  (score[1][0]["Cognitive"] / score[1][2]["Cognitive"]) * 100
                ),
                Math.round(
                  (score[1][0]["Affective"] / score[1][2]["Affective"]) * 100
                ),
                Math.round(
                  (score[1][0]["Empathic Response"] /
                    score[1][2]["Empathic Response"]) *
                    100
                ),
              ],
              backgroundColor: "rgba(255, 99, 132, 0.2)",
              borderColor: "rgb(255, 99, 132)",
              pointBackgroundColor: "rgb(255, 99, 132)",
              borderWidth: 2,
              fill: true,
            },
          ]
        : [
            {
              label: "Normalised Empathy Scores",
              data: [
                // change /100, /70, /50 to switch based on prac or user
                Math.round(
                  (score[0]["Cognitive"] / score[2]["Cognitive"]) * 100
                ),
                Math.round(
                  (score[0]["Affective"] / score[2]["Affective"]) * 100
                ),
                Math.round(
                  (score[0]["Empathic Response"] /
                    score[2]["Empathic Response"]) *
                    100
                ),
              ],
              backgroundColor: "rgba(54, 162, 235, 0.2)",
              borderColor: "rgb(54, 162, 235)",
              pointBackgroundColor: "rgb(54, 162, 235)",
              borderWidth: 2,
              fill: true,
            },
          ],
    };

    const config = {
      type: "radar",
      data: data,
      options: {
        responsive: true,
        layout: {
          padding: {
            bottom: 0, // reduce bottom margin
          },
        },
        plugins: {
          legend: {
            position: "top",
          },
        },
        scales: {
          r: {
            suggestedMin: 0,
            suggestedMax: 100,
            ticks: {
              stepSize: 20,
            },
          },
        },
      },
    };

    if (chartRef.current) {
      chartRef.current.destroy(); // destroy previous chart instance
    }

    chartRef.current = new Chart(canvasRef.current, config);
  }, [score]);

  const navigate = useNavigate();

  if (score.length === 0) {
    return <div>Calculating Empathy Score...</div>;
  }

  // from here check for first time doing quiz and logic accordingly
  let output = "";
  if (Array.isArray(score[1])) {
    const total =
      score[0][0]["Cognitive"] +
      score[0][0]["Affective"] +
      score[0][0]["Empathic Response"] +
      score[1][0]["Cognitive"] +
      score[1][0]["Affective"] +
      score[1][0]["Empathic Response"];

    const maxTotal =
      score[0][2]["Cognitive"] +
      score[0][2]["Affective"] +
      score[0][2]["Empathic Response"] +
      score[1][2]["Cognitive"] +
      score[1][2]["Affective"] +
      score[1][2]["Empathic Response"];

    let level = "";
    if (total > maxTotal * empathyScoreHighThreshold) {
      level = "high";
    } else if (
      total >= maxTotal * empathyScoreLowThreshold &&
      total <= maxTotal * empathyScoreHighThreshold
    ) {
      level = "moderate";
    } else {
      level = "low";
    }
    output = `${total} out of ${maxTotal}, indicating a ${level} level of empathy in general`;
  } else {
    const total =
      score[0]["Cognitive"] +
      score[0]["Affective"] +
      score[0]["Empathic Response"];

    const maxTotal =
      score[2]["Cognitive"] +
      score[2]["Affective"] +
      score[2]["Empathic Response"];

    let level = "";
    if (total > maxTotal * empathyScoreHighThreshold) {
      level = "high";
    } else if (
      total >= maxTotal * empathyScoreLowThreshold &&
      total <= maxTotal * empathyScoreHighThreshold
    ) {
      level = "moderate";
    } else {
      level = "low";
    }

    output = `${total} out of ${maxTotal}, indicating a ${level} level of empathy in general`;
  }

  // handling tool feedback questions
  const handleToolFeedbackSubmit = async (toolAnswers) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_FLASK_API_URL}/submit-feedback`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ answers: toolAnswers }),
        }
      );

      const result = await response.json();
      if (response.ok) {
        console.log("Tool feedback submitted:", result.message);
      } else {
        console.error("Failed to submit:", result.error);
      }
    } catch (error) {
      console.error("Submission error:", error);
    }
  };

  return (
    <div className="results-container">
      <header className="dash-header">
        {score[2] !== "guest" ? (
          <>
            <button onClick={() => navigate("/dashboard")}>Dashboard</button>
            <button onClick={() => navigate("/quiz-nav")}>Quiz</button>
            <button onClick={handleLogout}>Log Out</button>
          </>
        ) : (
          <>
            <button onClick={() => navigate("/consent")}>Quiz</button>
            <button onClick={() => navigate("/")}>Home</button>
          </>
        )}
      </header>
      <h2>
        Your total empathy score is {output} <br />
      </h2>
      {/* Summary */}
      <div className="results-summary">
        <b>Result Summary</b>: <br />
        {userDisplayProfile?.[1] && pracDisplayProfile?.[1] ? (
          <>
            <b>Practitioner-to-Practitioner Empathy</b>
            <br />
            {pracDisplayProfile[1]} <br />
            <br />
            {pracDisplayProfile[2]} <br />
            <br />
            <b>Practitioner-to-User Empathy</b>
            <br />
            {userDisplayProfile[1]} <br />
            <br />
            {userDisplayProfile[2]}
          </>
        ) : (
          <>
            {displayProfile?.[1]} <br />
            <br />
            {displayProfile?.[2]}
          </>
        )}
      </div>
      <div className="results-flex">
        {/* Score Descriptions - Right */}
        <div className="results-details">
          <p>
            {Array.isArray(score[1]) ? (
              <>
                <b>
                  Your Cognitive empathy score is: {score[0][0]["Cognitive"]}/
                  {score[0][2]["Cognitive"]} for Practitioner-to-Practitioner
                  empathy, and {score[1][0]["Cognitive"]}/
                  {score[1][2]["Cognitive"]} for Practitioner-to-User empathy
                </b>
                <br />
                {empathyDefinitions.cognitive}
                <br />
                <br />
                <b>
                  Your Affective empathy score is: {score[0][0]["Affective"]}/
                  {score[0][2]["Affective"]} for Practitioner-to-Practitioner
                  empathy, and {score[1][0]["Affective"]}/
                  {score[1][2]["Affective"]} for Practitioner-to-User empathy
                </b>
                <br />
                {empathyDefinitions.affective} <br />
                <br />
                <b>
                  Your Empathic Response score is:{" "}
                  {score[0][0]["Empathic Response"]}/
                  {score[0][2]["Empathic Response"]} for
                  Practitioner-to-Practitioner empathy, and{" "}
                  {score[1][0]["Empathic Response"]}/
                  {score[1][2]["Empathic Response"]} for Practitioner-to-User
                  empathy
                </b>
                <br />
                {empathyDefinitions.empathicResponse}
                <br />
              </>
            ) : (
              <>
                <b>
                  Your Cognitive score is: {score[0]["Cognitive"]}/
                  {score[2]["Cognitive"]}
                </b>
                <br />
                {empathyDefinitions.cognitive}
                <br />
                <br />
                <b>
                  Your Affective empathy score is: {score[0]["Affective"]}/
                  {score[2]["Affective"]}
                </b>
                <br />
                {empathyDefinitions.affective} <br />
                <br />
                <b>
                  Your Empathic Response score is:{" "}
                  {score[0]["Empathic Response"]}/
                  {score[2]["Empathic Response"]}
                </b>
                <br />
                {empathyDefinitions.empathicResponse}
                <br />
              </>
            )}
          </p>
        </div>

        {/* Radar Chart - Left */}
        <div className="results-chart">
          <canvas ref={canvasRef}></canvas>
        </div>
      </div>
      {/* recommendations */}
      <div className="recommendations-container">
        <UserRecommendations />
      </div>
      <hr />
      <ToolFeedback onSubmit={handleToolFeedbackSubmit} /> <br />
      <p className="disclaimer">
        <b>Disclaimer:</b> This empathy profile is based on your responses to
        the scale and is intended to provide insights into how you may engage
        with cognitive empathy, affective empathy, and empathic responses in
        your professional context. However, these results are not an absolute
        measure of your empathy skills, nor do they define your abilities as a
        practitioner. Empathy is complex, evolving, and influenced by various
        situational and personal factors. If your results are not what you
        expected, don’t be discouraged. Instead, consider them as an opportunity
        to reflect on different aspects of empathy and how they manifest in your
        work. Everyone has unique strengths, and empathy can be nurtured and
        developed over time. Use these insights as a tool for self-awareness and
        growth, rather than a strict evaluation of your capabilities.
      </p>
      <div style={{ marginTop: "1.5rem" }}>
        <button onClick={() => {
          if (localStorage.getItem("geminiConsent") === "true") {
            navigate("/gemini-chat");
          } else {
            navigate("/gemini-consent");
          }
        }}>
          Ask Gemini about your results
        </button>
      </div>
    </div>
  );
}

export default Results;